import {
  MaskedYearlessDateInput,
  MaskedYearlessDateInputChange,
  MaskedYearlessDateInputProps,
} from "./internal/MaskedYearlessDateInput";
import { useOptionallyControlledState } from "../../hooks";
import { validateYearlessDate } from "./internal/utils";
import { useMemo } from "react";
import { YearlessDate, YearlessDateMode } from "./types";

export type DateFieldYearlessChange = {
  value: YearlessDate | null;
  isInputValid: boolean;
  isInputEmpty: boolean;
  isValid: boolean;
};

export type DateFieldYearlessChangeHandler = (
  change: DateFieldYearlessChange,
) => void;

export type DateFieldYearlessProps = Omit<
  MaskedYearlessDateInputProps,
  "onChange" | "value" | "defaultValue" | "ref" | "required" | "autoComplete"
> & {
  value?: YearlessDate | null;
  defaultValue?: YearlessDate | null;
  onChange?: DateFieldYearlessChangeHandler;
  mode?: YearlessDateMode;
  disableHint?: boolean;
  required?: boolean;
  minDate?: YearlessDate | null;
  maxDate?: YearlessDate | null;
  unavailable?: {
    dates?: YearlessDate[];
  };
};

export const DateFieldYearless = ({
  value: valueProp,
  defaultValue: defaultValueProp,
  onChange,
  minDate,
  maxDate,
  unavailable,
  required,
  ...restProps
}: DateFieldYearlessProps) => {
  const [value, setValue] = useOptionallyControlledState<YearlessDate | null>({
    controlledValue: valueProp,
    defaultValue: defaultValueProp,
  });

  const currentValidity = useMemo(
    () =>
      validateYearlessDate({
        value: value ?? null,
        constraints: {
          required: required,
          unavailable: unavailable,
          minDate: minDate,
          maxDate: maxDate,
        },
      }),
    [value, required, unavailable, minDate, maxDate],
  );

  const handleInputChange = (change: MaskedYearlessDateInputChange) => {
    const { event, value, ...restChange } = change;
    setValue(value);
    return onChange?.({
      ...restChange,
      value,
      isValid: validateYearlessDate({
        value,
        constraints: {
          required: required,
          unavailable: unavailable,
          minDate: minDate,
          maxDate: maxDate,
        },
      }),
    });
  };

  return (
    <MaskedYearlessDateInput
      required={required}
      {...restProps}
      autoComplete="off"
      onChange={handleInputChange}
      value={value}
      data-valid={currentValidity}
    />
  );
};

DateFieldYearless.displayName = "DateFieldYearless";
